import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AuthService, IForgot } from '../auth.service';
import { startRequestCooldown } from '../passwordless';
import { AuthShellComponent } from '../shell';
import { ForgotFormInterface, ForgotFormValuesInterface } from './forgot.interface';

/**
 * Sends a password-reset email from the dedicated /forgot route, then shows the
 * "check your e-mail" state with a throttled resend.
 */
@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent],
  selector: 'app-forgot',
  templateUrl: './forgot.component.html',
})
export class ForgotComponent extends BaseService implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);

  formForgot: FormGroup;
  protected readonly sentTo = signal<string>('');
  protected readonly remainingSeconds = signal<number>(0);
  private stopCooldown: (() => void) | null = null;

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.stopCooldown?.();
  }

  createForm(): void {
    this.formForgot = new FormGroup<ForgotFormInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
    });
  }

  async sendResetLink(): Promise<void> {
    if (this.remainingSeconds() > 0) {
      return;
    }
    const formValues: ForgotFormValuesInterface = this.formForgot.getRawValue();
    const params: IForgot = { email: formValues.email };
    this._spin.start();
    await this.authService
      .forgot(params)
      .then(() => {
        this.sentTo.set(formValues.email);
        this.beginCooldown();
      })
      .catch(() => {
        this._toast.error({ message: 'Não foi possível enviar o e-mail, verifique os dados informados' });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  protected changeEmail(): void {
    this.stopCooldown?.();
    this.remainingSeconds.set(0);
    this.sentTo.set('');
  }

  private beginCooldown(): void {
    this.stopCooldown?.();
    this.stopCooldown = startRequestCooldown((remainingSeconds: number) => this.remainingSeconds.set(remainingSeconds));
  }
}
